import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { collection, query, where, orderBy, limit, onSnapshot, doc, updateDoc, writeBatch } from 'firebase/firestore';
import { db } from '../config/firebase';
import { useAuth } from '../context/AuthContext';

export default function NotificationBell() {
  const { currentUser } = useAuth();
  const navigate = useNavigate();
  const [notifications, setNotifications] = useState([]);
  const [showDropdown, setShowDropdown] = useState(false);
  const dropdownRef = useRef(null);

  const unreadCount = notifications.filter(n => !n.read).length;

  // Real-time listener for notifications
  useEffect(() => {
    if (!currentUser) {
      setNotifications([]);
      return;
    }

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', currentUser.uid),
      orderBy('createdAt', 'desc'),
      limit(20)
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
      setNotifications(notifs);
    }, (error) => {
      console.log('Notifications listener error:', error.message);
    });

    return () => unsubscribe();
  }, [currentUser]);

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const markAsRead = async (notifId) => {
    try {
      await updateDoc(doc(db, 'notifications', notifId), { read: true });
    } catch (err) {
      console.error('Error marking as read:', err);
    }
  };

  const markAllAsRead = async () => {
    try {
      const batch = writeBatch(db);
      notifications.filter(n => !n.read).forEach(n => {
        batch.update(doc(db, 'notifications', n.id), { read: true });
      });
      await batch.commit();
    } catch (err) {
      console.error('Error marking all as read:', err);
    }
  };

  const handleNotifClick = (notif) => {
    markAsRead(notif.id);
    setShowDropdown(false);

    const link = notif.data?.link;
    const raffleId = notif.data?.raffleId;

    if (link) {
      navigate(link);
    } else if (raffleId) {
      navigate(`/raffle/${raffleId}`);
    }
  };

  const getIcon = (type) => {
    switch (type) {
      case 'new_purchase': return '🔔';
      case 'purchase_pending': return '⏳';
      case 'payment_confirmed': return '✅';
      case 'payment_rejected': return '❌';
      case 'purchase_expired': return '⏰';
      case 'winner': return '🏆';
      default: return '📢';
    }
  };

  const timeAgo = (timestamp) => {
    if (!timestamp) return '';
    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
    const now = new Date();
    const diff = Math.floor((now - date) / 1000);

    if (diff < 60) return 'agora';
    if (diff < 3600) return `${Math.floor(diff / 60)}min`;
    if (diff < 86400) return `${Math.floor(diff / 3600)}h`;
    return `${Math.floor(diff / 86400)}d`;
  };

  if (!currentUser) return null;

  return (
    <div ref={dropdownRef} style={{ position: 'relative' }}>
      {/* Bell Button */}
      <button
        onClick={() => setShowDropdown(!showDropdown)}
        style={{
          background: 'none',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          padding: '0.4rem',
          fontSize: '1.3rem',
          lineHeight: 1,
        }}
        aria-label="Notificações"
      >
        🔔
        {unreadCount > 0 && (
          <span style={{
            position: 'absolute',
            top: '0',
            right: '-2px',
            background: '#d63031',
            color: 'white',
            borderRadius: '50%',
            width: '18px',
            height: '18px',
            fontSize: '0.7rem',
            fontWeight: 700,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            border: '2px solid white',
          }}>
            {unreadCount > 9 ? '9+' : unreadCount}
          </span>
        )}
      </button>

      {/* Dropdown */}
      {showDropdown && (
        <div style={{
          position: 'absolute',
          top: '100%',
          right: 0,
          width: '340px',
          maxHeight: '420px',
          overflowY: 'auto',
          background: 'white',
          borderRadius: '12px',
          boxShadow: '0 10px 40px rgba(0,0,0,0.15)',
          zIndex: 1000,
          border: '1px solid rgba(0,0,0,0.08)',
        }}>
          {/* Header */}
          <div style={{
            padding: '0.75rem 1rem',
            borderBottom: '1px solid #f0f0f0',
            display: 'flex',
            justifyContent: 'space-between',
            alignItems: 'center',
          }}>
            <strong style={{ fontSize: '0.95rem' }}>Notificações</strong>
            {unreadCount > 0 && (
              <button
                onClick={markAllAsRead}
                style={{
                  background: 'none', border: 'none', cursor: 'pointer',
                  color: '#6C5CE7', fontSize: '0.8rem', fontWeight: 600,
                }}
              >
                Marcar todas como lidas
              </button>
            )}
          </div>

          {/* Notifications List */}
          {notifications.length > 0 ? (
            notifications.map(notif => (
              <div
                key={notif.id}
                onClick={() => handleNotifClick(notif)}
                style={{
                  padding: '0.75rem 1rem',
                  borderBottom: '1px solid #f8f8f8',
                  cursor: 'pointer',
                  background: notif.read ? 'white' : 'rgba(108, 92, 231, 0.04)',
                  transition: 'background 0.2s',
                  display: 'flex',
                  gap: '0.75rem',
                  alignItems: 'flex-start',
                }}
                onMouseOver={(e) => e.currentTarget.style.background = '#f8f8f8'}
                onMouseOut={(e) => e.currentTarget.style.background = notif.read ? 'white' : 'rgba(108, 92, 231, 0.04)'}
              >
                <span style={{ fontSize: '1.3rem', flexShrink: 0, marginTop: '2px' }}>
                  {getIcon(notif.type)}
                </span>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{
                    fontWeight: notif.read ? 400 : 600,
                    fontSize: '0.85rem',
                    color: '#2d3436',
                    marginBottom: '2px',
                  }}>
                    {notif.title}
                  </div>
                  <div style={{
                    fontSize: '0.8rem',
                    color: '#636e72',
                    lineHeight: 1.4,
                    overflow: 'hidden',
                    textOverflow: 'ellipsis',
                    display: '-webkit-box',
                    WebkitLineClamp: 2,
                    WebkitBoxOrient: 'vertical',
                  }}>
                    {notif.message}
                  </div>
                  <div style={{ fontSize: '0.7rem', color: '#b2bec3', marginTop: '4px' }}>
                    {timeAgo(notif.createdAt)}
                  </div>
                </div>
                {!notif.read && (
                  <span style={{
                    width: '8px', height: '8px', borderRadius: '50%',
                    background: '#6C5CE7', flexShrink: 0, marginTop: '6px',
                  }} />
                )}
              </div>
            ))
          ) : (
            <div style={{
              padding: '2rem 1rem',
              textAlign: 'center',
              color: '#b2bec3',
              fontSize: '0.9rem',
            }}>
              <div style={{ fontSize: '2rem', marginBottom: '0.5rem' }}>🔕</div>
              Nenhuma notificação
            </div>
          )}
        </div>
      )}
    </div>
  );
}
